import { useState, useEffect, useCallback, useMemo } from 'react';
import type { TreeProps } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { getAncestors } from 'components/common/popover-tree-selector/tree-utils';

interface UsePopoverTreeExpansionParams {
  isPopoverOpen: boolean;
  selectedKey?: React.Key;
  searchMatchKeys: React.Key[];
  parentMap: Record<React.Key, React.Key | null>;
}

export const usePopoverTreeExpansion = ({
  isPopoverOpen,
  selectedKey,
  searchMatchKeys,
  parentMap
}: UsePopoverTreeExpansionParams) => {
  const [manuallyExpandedKeys, setManuallyExpandedKeys] = useState<React.Key[]>([]);

  const calculatedExpandedKeys = useMemo(() => {
    if (!isPopoverOpen) {
      return [];
    }

    if (!isEmpty(searchMatchKeys)) {
      const searchMatchAncestors = getAncestors(searchMatchKeys, parentMap);
      return [...searchMatchKeys, ...searchMatchAncestors];
    } else if (selectedKey) {
      const selectedKeyAncestors = getAncestors(selectedKey, parentMap);
      return selectedKeyAncestors;
    }

    return [];
  }, [isPopoverOpen, searchMatchKeys, selectedKey, parentMap]);

  useEffect(() => {
    if (!isPopoverOpen) {
      setManuallyExpandedKeys([]);
    }
  }, [isPopoverOpen]);

  const handleExpand: TreeProps['onExpand'] = useCallback((keys: React.Key[]) => {
    setManuallyExpandedKeys(keys);
  }, []);

  const expandedKeys = manuallyExpandedKeys.length > 0 ? manuallyExpandedKeys : calculatedExpandedKeys;

  return {
    expandedKeys,
    handleExpand
  };
};
